import { useQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import { FC, useCallback, useMemo } from 'react'
import { BROADCAST_CHANNEL, DEBOUNCE_DELAY } from 'constants/consts'
import { Autosuggest, AutosuggestProps } from 'components/Autosuggest/Autosuggest'
import { SEARCH_BY_STORYTELLER } from 'queries/successStory'
import {
  searchByStoryteller,
  searchByStorytellerVariables,
  searchByStoryteller_searchByStoryteller_items as Storyteller,
} from '__generatedTypes__/searchByStoryteller'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { useBroadcastChannel } from 'hooks/useBroadcastChannel'
import { REFETCH_SUCCESS_STORIES_MESSAGE } from 'pagesNew/SuccessStory/Create'
import { SuccessStoryRenderItem } from './SuccessStoryRenderItem'

type Props = {
  platform: Platform
  value: Storyteller[]
  onAdd: (successStory: Storyteller) => void
} & Partial<AutosuggestProps<Storyteller>>

export const SuccessStoriesSearch: FC<Props> = ({ platform, value, onAdd, ...rest }) => {
  const platformId = platform?.id

  const { data, refetch: refetchSuccessStories } = useQuery<searchByStoryteller, searchByStorytellerVariables>(
    SEARCH_BY_STORYTELLER,
    {
      fetchPolicy: 'network-only',
      variables: {
        platformId,
        searchString: '',
      },
    }
  )

  useBroadcastChannel(BROADCAST_CHANNEL, (event) => {
    if (event.data === REFETCH_SUCCESS_STORIES_MESSAGE) {
      refetchSuccessStories()
    }
  })

  const handleStorytellerInputChange = useCallback(
    debounce((searchString: string) => {
      refetchSuccessStories({
        searchString,
      })
    }, DEBOUNCE_DELAY),
    []
  )

  const filteredStorytellers = useMemo(() => {
    const addedSuccessStoriesIds = value.map(({ id }) => id)

    return (data?.searchByStoryteller?.items ?? []).filter(({ id }) => !addedSuccessStoriesIds.includes(id))
  }, [value, data])

  return (
    <Autosuggest<Storyteller>
      items={filteredStorytellers}
      onInputChange={handleStorytellerInputChange}
      getItemLabel={(successStory) => successStory.fullName}
      renderItem={(successStory) => <SuccessStoryRenderItem successStory={successStory} />}
      placeholder="Search User"
      searchable
      withoutCheckMark
      fullWidth
      onChange={(value) => onAdd(value[0])}
      value={value}
      {...rest}
    />
  )
}
